import React, { useState } from 'react';
import { useLibrary } from '../context/LibraryContext';
import { FaCloudUploadAlt, FaYoutube, FaMusic, FaSpinner } from 'react-icons/fa';
import '../styles/Upload.css';

const Upload = () => {
  const { uploadSong, downloadFromYoutube, loading, error } = useLibrary();
  const [activeTab, setActiveTab] = useState('file');
  const [fileUpload, setFileUpload] = useState({
    file: null,
    title: '',
    artist: '',
    album: '',
    genre: '',
    tags: ''
  });
  const [youtubeUpload, setYoutubeUpload] = useState({
    url: '',
    title: '',
    artist: '',
    album: '',
    genre: '',
    tags: ''
  });
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFileUpload({
        ...fileUpload,
        file,
        title: file.name.replace(/\.[^/.]+$/, '') // Remove extension
      });
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setFileUpload({
        ...fileUpload,
        file,
        title: file.name.replace(/\.[^/.]+$/, '') // Remove extension
      });
    }
  };

  // Handle file upload form input changes
  const handleFileInputChange = (e) => {
    const { name, value } = e.target;
    setFileUpload({
      ...fileUpload,
      [name]: value
    });
  };

  // Handle YouTube form input changes
  const handleYoutubeInputChange = (e) => {
    const { name, value } = e.target;
    setYoutubeUpload({
      ...youtubeUpload,
      [name]: value
    });
  };

  // Submit file upload
  const handleFileUpload = async (e) => {
    e.preventDefault();
    setUploadError('');
    setUploadSuccess(false);
    
    if (!fileUpload.file) {
      setUploadError('Veuillez sélectionner un fichier audio');
      return;
    }
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('files', fileUpload.file); // Utilisation de 'files' pour correspondre à la configuration du serveur
      formData.append('title', fileUpload.title);
      formData.append('artist', fileUpload.artist);
      formData.append('album', fileUpload.album);
      formData.append('genre', fileUpload.genre);
      formData.append('tags', fileUpload.tags);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      // Upload the song
      const result = await uploadSong(formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadSuccess(true);
      
      // Reset form after successful upload
      setTimeout(() => {
        setFileUpload({
          file: null,
          title: '',
          artist: '',
          album: '',
          genre: '',
          tags: ''
        });
        setUploadProgress(0);
      }, 2000);
      
    } catch (err) {
      setUploadError('Erreur lors du téléchargement: ' + (err.message || 'Erreur inconnue'));
      setUploadProgress(0);
    }
  };

  // Submit YouTube download
  const handleYoutubeDownload = async (e) => {
    e.preventDefault();
    setUploadError('');
    setUploadSuccess(false);
    
    if (!youtubeUpload.url) {
      setUploadError('Veuillez entrer une URL YouTube valide');
      return;
    }
    
    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 500);
      
      // Download from YouTube
      const result = await downloadFromYoutube({
        url: youtubeUpload.url,
        title: youtubeUpload.title,
        artist: youtubeUpload.artist,
        album: youtubeUpload.album,
        genre: youtubeUpload.genre,
        tags: youtubeUpload.tags
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadSuccess(true);
      
      // Reset form after successful download
      setTimeout(() => {
        setYoutubeUpload({
          url: '',
          title: '',
          artist: '',
          album: '',
          genre: '',
          tags: ''
        });
        setUploadProgress(0);
      }, 2000);
      
    } catch (err) {
      setUploadError('Erreur lors du téléchargement: ' + (err.message || 'Erreur inconnue'));
      setUploadProgress(0);
    }
  };

  return (
    <div className="upload-page">
      <h1>Ajouter de la musique</h1>
      
      <div className="upload-tabs">
        <button 
          className={`upload-tab ${activeTab === 'file' ? 'active' : ''}`}
          onClick={() => setActiveTab('file')}
        >
          <FaCloudUploadAlt /> Fichier audio
        </button>
        <button 
          className={`upload-tab ${activeTab === 'youtube' ? 'active' : ''}`}
          onClick={() => setActiveTab('youtube')}
        >
          <FaYoutube /> YouTube
        </button>
      </div>
      
      <div className="upload-content">
        {activeTab === 'file' ? (
          <form onSubmit={handleFileUpload} className="upload-form">
            <div 
              className={`file-drop-area ${dragActive ? 'drag-active' : ''} ${fileUpload.file ? 'has-file' : ''}`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
            >
              <input 
                type="file" 
                id="file-upload" 
                accept=".mp3,.wav"
                onChange={handleFileChange}
                className="file-input"
              />
              <label htmlFor="file-upload" className="file-label">
                {fileUpload.file ? (
                  <>
                    <FaMusic className="file-icon" />
                    <span className="file-name">{fileUpload.file.name}</span>
                    <span className="file-size">({(fileUpload.file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                  </>
                ) : (
                  <>
                    <FaCloudUploadAlt className="upload-icon" />
                    <span>Glissez votre fichier audio ici ou cliquez pour parcourir</span>
                    <span className="file-types">Formats supportés: MP3, WAV</span>
                  </>
                )}
              </label>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="title">Titre *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={fileUpload.title}
                  onChange={handleFileInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="artist">Artiste</label>
                <input
                  type="text"
                  id="artist"
                  name="artist"
                  value={fileUpload.artist}
                  onChange={handleFileInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="album">Album</label>
                <input
                  type="text"
                  id="album"
                  name="album"
                  value={fileUpload.album}
                  onChange={handleFileInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="genre">Genre</label>
                <input
                  type="text"
                  id="genre"
                  name="genre"
                  value={fileUpload.genre}
                  onChange={handleFileInputChange}
                />
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="tags">Tags (séparés par des virgules)</label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={fileUpload.tags}
                  onChange={handleFileInputChange}
                  placeholder="ex: chill, été, acoustique"
                />
              </div>
            </div>
            
            <button 
              type="submit" 
              className="btn-upload"
              disabled={loading || !fileUpload.file}
            >
              {loading ? <FaSpinner className="spinner" /> : <FaCloudUploadAlt />} 
              Télécharger
            </button>
          </form>
        ) : (
          <form onSubmit={handleYoutubeDownload} className="upload-form">
            <div className="form-group full-width">
              <label htmlFor="url">URL YouTube *</label>
              <input
                type="text"
                id="url"
                name="url"
                value={youtubeUpload.url}
                onChange={handleYoutubeInputChange}
                placeholder="https://www.youtube.com/watch?v=..."
                required
              />
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="yt-title">Titre</label>
                <input
                  type="text"
                  id="yt-title"
                  name="title"
                  value={youtubeUpload.title}
                  onChange={handleYoutubeInputChange}
                  placeholder="Sera extrait automatiquement si vide"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="yt-artist">Artiste</label>
                <input
                  type="text"
                  id="yt-artist"
                  name="artist"
                  value={youtubeUpload.artist}
                  onChange={handleYoutubeInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="yt-album">Album</label>
                <input
                  type="text"
                  id="yt-album"
                  name="album"
                  value={youtubeUpload.album}
                  onChange={handleYoutubeInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="yt-genre">Genre</label>
                <input
                  type="text"
                  id="yt-genre"
                  name="genre"
                  value={youtubeUpload.genre}
                  onChange={handleYoutubeInputChange}
                />
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="yt-tags">Tags (séparés par des virgules)</label>
                <input
                  type="text"
                  id="yt-tags"
                  name="tags"
                  value={youtubeUpload.tags}
                  onChange={handleYoutubeInputChange}
                  placeholder="ex: chill, été, acoustique"
                />
              </div>
            </div>
            
            <button 
              type="submit" 
              className="btn-upload youtube"
              disabled={loading || !youtubeUpload.url}
            >
              {loading ? <FaSpinner className="spinner" /> : <FaYoutube />} 
              Télécharger depuis YouTube
            </button>
          </form>
        )}
        
        {uploadProgress > 0 && (
          <div className="upload-progress-container">
            <div 
              className="upload-progress-bar" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
            <span className="upload-progress-text">{uploadProgress}%</span>
          </div>
        )}
        
        {uploadSuccess && (
          <div className="upload-success">
            Téléchargement réussi !
          </div>
        )}
        
        {(uploadError || error) && (
          <div className="upload-error">
            {uploadError || error}
          </div>
        )}
      </div>
    </div>
  );
};

export default Upload;
